import PropTypes from "prop-types";
import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from "react-table";

import { Col, Row, Spinner, Table } from "reactstrap";
import { DefaultColumnFilter } from "./filters";

import { formatNumberWithCommas } from "helpers/format";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import TooltipCustom from "./TooltipCustom";
import { useDraggable } from "react-use-draggable-scroll";
import moment from "moment";
import LabelWrapper from "./LabelWrapper";
import DatePickerCustom from "./DatePickerCustom";
import RankingChart from "pages/News/TrackingNews/RankingChart";
import { toast } from "react-toastify";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { getChartDailySearchVolumeRankingKeywordNews, getChartRankingKeywordNews } from "store/thunks";

interface TableTrackingNewsContainerProps {
  className?: any;
  columns?: any;
  data?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  scrollHorizontalAuto?: boolean;
  description?: string;
  customPageIndex?: any;
  customPageCount?: any;
  totalRecords?: any;
  handleChangePage?: any;
  sorting?: { sort_by: string, order_by: string };
  handleChangeSorting?: (sortBy: { sort_by: string | undefined | null, order_by: string | undefined | null }) => void,
  manualSorting?: boolean,
  isLoading?: boolean,
  isShowPagination?: boolean,
  isShowPaginationOnTop?: boolean,
  isShowLoadingBottom?: boolean,
  isShowColumn?: boolean,
  classBackgroundWhenActiveSort?: string,
  isScrollToTop?: boolean,
  isShowHeaderTableFixedTab?: string;
  newsIdOpen?: string
}

const TableTrackingNewsContainer = ({
  columns = [],
  data = [],
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  scrollHorizontalAuto = false,
  description,
  customPageIndex,
  customPageCount,
  handleChangePage,
  totalRecords,
  sorting,
  handleChangeSorting,
  manualSorting = false,
  isLoading = false,
  isShowPagination = true,
  isShowPaginationOnTop = false,
  isShowLoadingBottom = false,
  isShowColumn = true,
  classBackgroundWhenActiveSort = '',
  isScrollToTop = true,
  isShowHeaderTableFixedTab = '',
  newsIdOpen = ''
}: TableTrackingNewsContainerProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: +customPageIndex || 0,
        pageSize: +customPageSize || 0,
        selectedRowIds: 0,
      },
      pageCount: +customPageCount || 0,
      manualPagination: true,
      // manualSorting: true,
      manualSortBy: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );
  const { t } = useTranslation();

  const [isScrollBottom, setIsScrollBottom] = useState<boolean>(false);
  const [scrollLeft, setScrollLeft] = useState<any>(0);

  const thRefs = useRef<any>([]);
  const tableRef = useRef<any>(null);
  const warperTableRef = useRef<any>(null);
  const elementFixedRef = useRef<any>(null);
  const elementHeaderFixedRef = useRef<any>(null);
  const elementTable: any = tableRef?.current?.getBoundingClientRect() || {};
  const elementWarperTable: any = warperTableRef?.current?.getBoundingClientRect() || {};

  const [isShowHeaderTableFixed, setIsShowHeaderTableFixed] = useState<boolean>(false);

  const { events } = useDraggable(warperTableRef);

  const generateSortingIndicator = (column: any) => {
    return (
      column?.sortable ? (
        <span style={{ display: 'inline-flex', justifyContent: 'center', width: '12px', marginLeft: '2px', verticalAlign: 'middle' }}>
          {(column.id === sorting?.sort_by && sorting?.order_by === 'ASC') && <i className={`ri-arrow-up-line fs-13 text-primary`} />}
          {(column.id === sorting?.sort_by && sorting?.order_by === 'DESC') && <i className={`ri-arrow-down-line fs-13 text-primary`} />}
        </span>
      ) : (
        ""
      )
    );
  };
  const generateDescription = (column: any) => {
    return (
      column?.description ? (
        <TooltipCustom
          title={column?.description || ''}
          className="d-inline-block vertical-align-middle ms-1"
          style={{ transform: 'translateY(2px)' }}
        >
          <i className="ri-question-line align-bottom text-secondary" ></i>
        </TooltipCustom>
      ) : (<></>)
    );
  };

  useEffect(() => {
    if (isShowHeaderTableFixedTab) {
      setIsShowHeaderTableFixed(false);
    }
  }, [isShowHeaderTableFixedTab]);

  const handleScroll = (e: any) => {
    setScrollLeft((prev: any) => e?.target?.scrollLeft)
    if (warperTableRef?.current && elementFixedRef?.current) {
      // If warperTableRef scrolls, then scroll elementFixedRef as well
      if (e.target === warperTableRef.current) {
        elementFixedRef.current.scrollLeft = e?.target?.scrollLeft;
        elementHeaderFixedRef.current.scrollLeft = e?.target?.scrollLeft - (window.innerWidth < 500 ? 3 : 0);
      }
      // If elementFixedRef scrolls, then scroll warperTableRef as well
      else if (e.target === elementFixedRef.current) {
        warperTableRef.current.scrollLeft = e?.target?.scrollLeft;
      }
    }

  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document?.documentElement?.scrollTop || 0;
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const MARGIN_BOTTOM_HEIGHT = 170; // 170px
      if ((scrollTop + windowHeight + MARGIN_BOTTOM_HEIGHT) >= documentHeight) {
        setIsScrollBottom((prev) => true);
      } else {
        setIsScrollBottom((prev) => false);
      }

      const rect = warperTableRef.current.getBoundingClientRect();
      const distanceFromTop = rect.top;
      if (distanceFromTop <= 70) {
        elementHeaderFixedRef.current.scrollLeft = elementFixedRef?.current?.scrollLeft - (window.innerWidth < 500 ? 3 : 0);
        setIsShowHeaderTableFixed((prev) => true);
      } else {
        setIsShowHeaderTableFixed((prev) => false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Fragment>
      {isShowPaginationOnTop && (
        <div className="w-100 d-flex align-items-center justify-content-end mt-0 mb-4 pb-1 text-sm-end">
          {/* <div className="col-sm">
            <div className="text-muted">
              {t('Total')}:
              {" "}
              <span className="fw-semibold ms-1">{formatNumberWithCommas(totalRecords)}</span>
              {" "}
              {t('Results')}
            </div>
          </div> */}
          <div className="col-sm-auto overflow-x-auto">
            <ReactPaginate
              nextLabel={`${t('next')} >`}
              onPageChange={(page) => {
                if (handleChangePage) {
                  isScrollToTop && handleScrollToTop();
                  handleChangePage(page.selected);
                }
              }}
              forcePage={pageIndex}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={customPageCount}
              previousLabel={`< ${t('previous')}`}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination pagination-separated"
              activeClassName="active"
              renderOnZeroPageCount={null}
            />
          </div>
        </div>)}
      <div className={`table-custom-pagination ${divClass}`} {...events} ref={warperTableRef} onScroll={handleScroll}>
        <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'}`} style={{ minHeight: '70px' }} >
          <thead className={theadClass} ref={tableRef}>
            {headerGroups.map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any, i: number) => (
                  <th
                    key={column.id}
                    ref={el => thRefs.current[i] = el}
                    className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                    {...(column?.thWidth ? { width: column?.thWidth } : {})}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    onClick={(e) => {

                      if (!column?.sortable) { return };

                      if ((!sorting?.sort_by || !sorting?.order_by)) {
                        const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                        handleChangeSorting && handleChangeSorting(sortBy);
                      } else if (sorting?.sort_by && sorting?.order_by) {
                        const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                        handleChangeSorting && handleChangeSorting(sortBy);
                      }
                    }}
                  >

                    {column?.thComponent ? <>{column?.thComponent()}</> : column.render("Header")}
                    {generateDescription(column)}
                    {generateSortingIndicator(column)}

                  </th>
                ))}
              </tr>
            ))}
            {(headerGroups?.length) && (<tr >
              <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                  <div className="infinite-loading-inner"></div>
                </div>
              </th>
            </tr>)}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page?.map((row: any) => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <Tr row={row} />
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      <div
        className={`table-custom-pagination table-card overflow-x-auto m-0 hidden-scrollbar-os ${isShowHeaderTableFixed ? '' : 'd-none'}`}
        style={{
          position: 'fixed',
          top: 70,
          zIndex: 9,
          left: elementWarperTable?.left,
          width: elementWarperTable?.width,
          right: 0,

        }}
        ref={elementHeaderFixedRef}
      >
        <Table hover {...getTableProps()} className={`${tableClass} ${Number(scrollLeft || 0) > 0 && 'is-scroll'} mb-0`} style={{ height: 'auto', tableLayout: 'fixed' }} >
          <thead className={theadClass} style={{ width: elementTable?.width }}>
            {headerGroups.map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}  {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any, i: number) => (
                  <th
                    key={column.id}
                    className={`${thClass || ''} ${column?.thClass || ''} ${(column?.sortable && column.id === sorting?.sort_by) && classBackgroundWhenActiveSort}`}
                    {...(column?.thWidth ? { width: thRefs.current[i]?.offsetWidth } : { width: thRefs.current[i]?.offsetWidth })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    onClick={(e) => {

                      if (!column?.sortable) { return };

                      if ((!sorting?.sort_by || !sorting?.order_by)) {
                        const sortBy = { sort_by: column?.id, order_by: 'DESC' };
                        handleChangeSorting && handleChangeSorting(sortBy);
                      } else if (sorting?.sort_by && sorting?.order_by) {
                        const sortBy = { sort_by: column?.id, order_by: sorting?.order_by === 'DESC' && sorting?.sort_by === column?.id ? 'ASC' : 'DESC' };
                        handleChangeSorting && handleChangeSorting(sortBy);
                      }
                    }}
                  >

                    {column?.thComponent ? <>{column?.thComponent()}</> : column.render("Header")}
                    {generateDescription(column)}
                    {generateSortingIndicator(column)}

                  </th>
                ))}
              </tr>
            ))}
            {(headerGroups?.length) && (<tr >
              <th colSpan={headerGroups[0]?.headers?.length} className="py-0 px-0" style={{ borderBottomColor: 'transparent' }}>
                <div className={`infinite-loading-outer ${isLoading && 'is-show-loading'}`}>
                  <div className="infinite-loading-inner"></div>
                </div>
              </th>
            </tr>)}
          </thead>
        </Table>
      </div>
      <div
        className="overflow-x-auto color-scrollbar-os"
        style={{
          position: 'fixed',
          zIndex: 10,
          left: elementWarperTable?.left,
          bottom: 0,
          width: elementWarperTable?.width,
          right: 0,
          opacity: isScrollBottom ? 0 : 1,
        }}
        ref={elementFixedRef}
        onScroll={handleScroll}
      >
        <div style={{ width: elementTable?.width, height: '1px' }}></div>
      </div>

      {(isShowLoadingBottom && isLoading) && (<div className=" d-flex text-center align-items-center justify-content-center pt-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="100px" height="60px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
          <g transform="translate(20 50)">
            <circle cx="0" cy="0" r="10" fill="#3498db">
              <animateTransform attributeName="transform" type="scale" begin="-0.375s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(40 50)">
            <circle cx="0" cy="0" r="10" fill="#53a6de">
              <animateTransform attributeName="transform" type="scale" begin="-0.25s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(60 50)">
            <circle cx="0" cy="0" r="10" fill="#5fb5ef">
              <animateTransform attributeName="transform" type="scale" begin="-0.125s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g><g transform="translate(80 50)">
            <circle cx="0" cy="0" r="10" fill="#5ca6d8">
              <animateTransform attributeName="transform" type="scale" begin="0s" calcMode="spline" keySplines="0.3 0 0.7 1;0.3 0 0.7 1" values="0;1;0" keyTimes="0;0.5;1" dur="1s" repeatCount="indefinite"></animateTransform>
            </circle>
          </g>
        </svg>
      </div>)}
      {isShowPagination && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">
              {t('Total')}:
              {" "}
              <span className="fw-semibold ms-1">{formatNumberWithCommas(totalRecords)}</span>
              {" "}
              {t('Results')}
            </div>
          </div>
          <div className="col-sm-auto overflow-x-auto">
            <ReactPaginate
              nextLabel={`${t('next')} >`}
              onPageChange={(page) => {
                if (handleChangePage) {
                  isScrollToTop && handleScrollToTop();
                  handleChangePage(page.selected);
                }
              }}
              forcePage={pageIndex}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={customPageCount}
              previousLabel={`< ${t('previous')}`}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination pagination-separated"
              activeClassName="active"
              renderOnZeroPageCount={null}
            />
          </div>
        </Row>)}
    </Fragment>
  );
};

TableTrackingNewsContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default React.memo(TableTrackingNewsContainer);


const Tr = React.memo(({ row }: any) => {

  // const { t } = useTranslation();

  // const [isOpenChart, setIsOpenChart] = useState<boolean>(false);

  // const [isChartLoading, setIsChartLoading] = useState<boolean>(false);

  // const [isSameYChart, setIsSamYChart] = useState<boolean>(false);

  // const [chart, setChart] = useState<any>(null);

  // const [dateRangeChartSearch, setDateRangeChartSearch] = useState<any[]>([moment().toDate(), moment().toDate()]);
  // const [startDateChart, endDateChart] = dateRangeChartSearch;

  // const handleGetDataChart = async (_chart: any) => {
  //   try {
  //     setIsChartLoading((_prev) => true);
  //     const _data_form = {
  //       news_id: _chart?.news_id,
  //       date_start: _chart?.date_start ? _chart?.date_start : (startDateChart ? moment(startDateChart).format("Y-MM-DD") : ''),
  //       date_end: _chart?.date_end ? _chart?.date_end : (endDateChart ? moment(endDateChart).format("Y-MM-DD") : ''),
  //     };
  //     const _data_form_daily = {
  //       ..._data_form,
  //       date_start: (_data_form?.date_start === _data_form?.date_end) ? moment(_data_form?.date_end, "Y-MM-DD").subtract(7, 'days').format("Y-MM-DD") : _data_form?.date_start
  //     };
  //     const [res1, res2]: any = await Promise.all([getChartDailySearchVolumeRankingKeywordNews(_data_form_daily), getChartRankingKeywordNews(_data_form)]);;
  //     setIsChartLoading((_prev) => false);
  //     setIsSamYChart((_prev) => moment(startDateChart).format("Y-MM-DD") === moment(endDateChart).format("Y-MM-DD"));
  //     if (res1?.code === 200 || res2?.code === 200) {
  //       const _data_chart_daily = res1?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['daily_search_volume'] || 0) }));

  //       const _data_chart_impression = res2?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['impression_count'] || 0) }));
  //       const _data_chart_click = res2?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['click_count'] || 0) }));
  //       const _data_chart_ctr = res2?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['ctr_count'] || 0) }));
  //       const _data_chart_impression_rate = res2?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['impression_rate'] || 0) }));
  //       const _data_chart_rank = res2?.data?.map((c: any) => ({ date: c?.col, index: Number(c?.['exposure_rank'] || 0) }));
  //       setChart((_prev: any) => ({
  //         data_chart_daily: _data_chart_daily,

  //         data_chart_impression: _data_chart_impression,
  //         data_chart_click: _data_chart_click,
  //         data_chart_ctr: _data_chart_ctr,
  //         data_chart_impression_rate: _data_chart_impression_rate,
  //         data_chart_rank: _data_chart_rank,
  //       }));
  //     } else {
  //       toast(`${res2}`, CONFIG_OPTION_TOAST_ERROR);
  //     }
  //   } catch (error: any) {
  //     setIsChartLoading((_prev) => false);
  //     toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
  //     return error;
  //   }
  // };

  // useEffect(() => {
  //   if (!!isOpenChart && !!row?.original?.news_id) {
  //     handleGetDataChart({ news_id: row?.original?.news_id });
  //   }
  // }, [isOpenChart, row?.original?.news_id]);

  // // useEffect(() => {
  // //   return () => {
  // //     setIsOpenChart((_prev) => false);
  // //   }
  // // }, []);

  // const handleSearchDataChart = async () => {
  //   handleGetDataChart({ news_id: row?.original?.news_id });
  // };

  // const handleOpenChart = async () => {
  //   setIsOpenChart((_prev) => !_prev);
  // };

  // event the first tr :  onClick={() => !row?.original?.text_row && handleOpenChart()}

  return (<>
    <tr>
      {(row?.cells || []).map((cell: any) => {
        return (
          <td key={cell.id} {...cell.getCellProps()} >
            {cell.render("Cell")}
          </td>
        );
      })}
    </tr>
    {/* {(!row?.original?.text_row && isOpenChart) && <tr className="tr-none-hover">
      <td colSpan={row?.cells?.length} className="bg-row-chill">
        <div className="position-absolute"
          style={{
            right: '0px',
          }}>
          <button className="btn btn-secondary btn-icon p-0"
            type="button"
            style={{
              width: '48px',
              transform: 'translateY(-12px)',
              height: '48px',
              borderRadius: '0px',
              borderBottomLeftRadius: '24px',
            }}
            onClick={() => handleOpenChart()}
          >
            <i className="ri ri-close-line fs-24 cursor-pointer" />
          </button>
        </div>
        <div className="position-absolute"
          style={{
            left: '20px',
            width: '500px'
          }}>
          <Row className="align-items-end">
            <Col sm={12} md={12} className='date-picker-wrapper-custom mt-3 mt-xxl-2' style={{ width: '250px' }}>
              <LabelWrapper label={t('Date')} isShow={!!startDateChart || !!endDateChart}>
                <DatePickerCustom
                  placeholder={`${t('Start Date')} - ${t('End Date')}`}
                  startDate={startDateChart || null}
                  endDate={endDateChart || null}
                  onChangePicker={(values: any) => {
                    setDateRangeChartSearch((_prev: any) => values);
                  }}
                  showOptions={[
                    'today', 'yesterday',
                    'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                    'this_week', 'last_week',
                    'this_month', 'last_month',
                  ]}
                />
              </LabelWrapper>
            </Col>
            <Col sm={4} md={6}>
              <button
                type="button"
                className="btn btn-info me-2 me-lg-2"
                onClick={() => handleSearchDataChart()}
                disabled={isChartLoading}
              >
                <i className="ri-search-line align-bottom me-1"></i>{" "}
                {t('Button Search')}
              </button>
            </Col>
          </Row>
        </div>
        <Row className="mt-5 child-row-animation">
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('Impression Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_impression}
                name={t('Impression')}
                height={window.innerWidth < 750 ? 250 : 350}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={false}
                isShowReversedY={false}
                typeChart='line'

              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('Click Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_click}
                name={t('Click')}
                height={window.innerWidth < 750 ? 250 : 350}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={false}
                isShowReversedY={false}
                typeChart='line'
              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('CTR Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_ctr}
                name={t('CTR')}
                height={window.innerWidth < 750 ? 250 : 350}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={true}
                isShowReversedY={false}
                typeChart='line'
              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('Impression Rate Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_impression_rate}
                name={t('Impression Rate')}
                height={window.innerWidth < 750 ? 250 : 350}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={true}
                isShowReversedY={false}
                typeChart='line'
              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('Rank Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_rank}
                name={t('Rank')}
                height={window.innerWidth < 750 ? 250 : 350}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={false}
                isShowReversedY={true}
                typeChart='line'
              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
          <Col sm={2}>
            <div className="mt-3 text-center text-info-dark mb-0">{t('Daily Search Volume Trend')}</div>
            <div style={{ minHeight: "170px" }} className="position-relative overflow-x-auto overflow-x-lg-hidden overflow-y-hidden">
              <RankingChart
                data={chart?.data_chart_daily}
                name={t('Daily Search Volume')}
                height={window.innerWidth < 750 ? 250 : (isSameYChart ? 320 : 350)}
                style={{ transform: 'translateY(0px)', minWidth: window.innerWidth < 750 ? '900px' : 'auto' }}
                isShowXaxis={true}
                isShowYaxis={true}
                isShowTooltip={true}
                showAxisBorder={true}
                fillColor="#4274FB"
                chartColor='#4274FB'
                fillOpacity={1}
                markersSize={3}
                isRotate={true}
                tickAmountXaxis={30}
                isPercentYaxis={false}
                isShowReversedY={false}
                typeChart='column'
              />
              {isChartLoading && <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}><Spinner size="sm" className="text-info-blue fs-20 align-middle" style={{ width: '50px', height: '50px' }}></Spinner></div>}
            </div>
          </Col>
        </Row>
      </td>
    </tr >} */}
  </>
  )
});